import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { hrefFor, type NavigableRouteKey } from '@omnea-app/fe-authz/routeHref'
import { useRouteAccess } from '@omnea-app/fe-authz/useRouteAccess'
import { isRouteActive } from '@omnea-app/fe-routing/routes/isRouteActive'
import type { MenuItemType } from './SidebarUncontrolled'

type RouteNavItem<TCount extends string> = {
  route: NavigableRouteKey
  labelKey: string
  Icon: React.ElementType
  /**
   * The sections this item highlights for, where they are not simply the path it links to — a legacy
   * address it still owns.
   *
   * TODO(PLT-1727): replace with route keys once those addresses are keyed.
   */
  activePaths?: readonly string[]
  count?: TCount
  badgeKey?: string
}

type GroupNavItem<TCount extends string> = {
  labelKey: string
  Icon: React.ElementType
  children: readonly RouteNavItem<TCount>[]
}

export type NavItem<TCount extends string> = RouteNavItem<TCount> | GroupNavItem<TCount>

const isGroup = <TCount extends string>(item: NavItem<TCount>): item is GroupNavItem<TCount> => 'children' in item

export const useSidebarMenuItems = <TCount extends string>(
  navItems: readonly NavItem<TCount>[],
  countsByName: Readonly<Record<TCount, number | null>>
): MenuItemType[] => {
  const { t } = useTranslation('common')
  const { asPath } = useRouter()
  const routeAccess = useRouteAccess()

  const toMenuItem = (item: RouteNavItem<TCount>): MenuItemType => {
    const href = hrefFor(item.route)

    return {
      title: t(item.labelKey),
      Icon: item.Icon,
      href,
      selected: (item.activePaths ?? [href]).some((section) => isRouteActive(asPath, section)),
      count: item.count == null ? undefined : (countsByName[item.count] ?? undefined),
      badge: item.badgeKey == null ? undefined : t(item.badgeKey),
    }
  }

  const isVisible = (item: RouteNavItem<TCount>) => routeAccess.get(item.route) === 'allowed'

  return navItems.reduce<MenuItemType[]>((visible, item) => {
    if (!isGroup(item)) {
      return isVisible(item) ? [...visible, toMenuItem(item)] : visible
    }

    const children = item.children.filter(isVisible).map(toMenuItem)
    const [firstChild] = children
    if (firstChild == null || children.length === 1) return [...visible, ...children]

    return [
      ...visible,
      {
        title: t(item.labelKey),
        Icon: item.Icon,
        href: firstChild.href,
        selected: children.some((child) => child.selected),
        children,
      },
    ]
  }, [])
}
